import { useEffect, useState, useCallback } from "react";

import { Alert } from "@/components/alert/alert";
import { PokemonSprite } from "@/components/pokemon/pokemon-sprite";
import { Button } from "@/components/ui/buttons/button";
import { useBackAction } from "@/hooks/use-back-action";
import { useAuth } from "@/providers/auth-provider";
import { useSPAContext } from "@/providers/spa-provider";
import { listPokeTeams } from "@/services/poketeam/list-poketeams";
import { startTrainingBattle } from "@/services/training/start-training-battle";
import type { PokeTeam } from "@/types";

import { DojoBattleView } from "./dojo-battle-view";
import { DojoCustomSetupView } from "./dojo-custom-setup-view";
import { DojoTeamSelectCard } from "./dojo-team-select-card";
import {
  readDojoTrainingConfig,
  type DojoTrainingConfig,
} from "./dojo-training-config-view";

import type { CompleteBattleLog } from "types/battle-engine";


type DojoStep = "select-team" | "select-pokemon" | "battle";

/**
 * Dojo Team Selection View - Select a team and 3 Pokémon for training
 */
export const DojoTeamSelectView = () => {
  const { setCurrentRoute, previousRoute } = useSPAContext();
  const { getAccessToken } = useAuth();

  const [step, setStep] = useState<DojoStep>("select-team");
  const [teams, setTeams] = useState<PokeTeam[]>();
  const [selectedTeam, setSelectedTeam] = useState<PokeTeam | null>(null);
  const [selectedPokemonIds, setSelectedPokemonIds] = useState<number[]>([]);
  const [config] = useState<DojoTrainingConfig>(() => readDojoTrainingConfig());
  const [isStarting, setIsStarting] = useState(false);
  const [battleData, setBattleData] = useState<{
    battleLog: CompleteBattleLog;
    botName: string;
    botCountry: string | null;
  } | null>(null);

  const handleBack = useCallback(() => {
    if (step === "battle") {
      setStep("select-pokemon");
      setBattleData(null);
    } else if (step === "select-pokemon") {
      setStep("select-team");
      setSelectedTeam(null);
      setSelectedPokemonIds([]);
    } else {
      setCurrentRoute(previousRoute);
    }
  }, [step, previousRoute, setCurrentRoute]);

  useBackAction(handleBack);

  const getTeams = useCallback(async () => {
    const accessToken = getAccessToken();
    if (!accessToken) {
      Alert("Authentication error. Please try again");
      return;
    }

    const response = await listPokeTeams(accessToken);

    if (response.ok && response.data?.teams) {
      setTeams(response.data.teams);
    } else {
      Alert("Failed to load teams");
    }
  }, [getAccessToken]);

  const handleTeamSelect = useCallback((team: PokeTeam) => {
    if (team.pokemons.length < 3) {
      Alert("This team needs at least 3 Pokémon for training");
      return;
    }
    setSelectedTeam(team);
    setSelectedPokemonIds([]);
    setStep("select-pokemon");
  }, []);

  const handlePokemonToggle = useCallback((pokemonId: number) => {
    setSelectedPokemonIds((prev) => {
      if (prev.includes(pokemonId)) {
        return prev.filter((id) => id !== pokemonId);
      }
      if (prev.length >= 3) {
        return prev; // Already 3 selected
      }
      return [...prev, pokemonId];
    });
  }, []);

  const handleStartBattle = useCallback(async () => {
    const required = config.format === "1v1" ? 1 : 3;
    if (selectedPokemonIds.length !== required) {
      Alert(`Please select exactly ${required} Pokémon`);
      return;
    }

    const accessToken = getAccessToken();
    if (!accessToken) {
      Alert("Authentication error. Please try again");
      return;
    }

    setIsStarting(true);
    try {
      const response = await startTrainingBattle(accessToken, {
        pokemon_ids: selectedPokemonIds,
        mode: "quick_training",
        format: config.format,
        cpu_type: config.cpuType,
      });

      if (response.ok && response.data) {
        setBattleData({
          battleLog: response.data.battle_log,
          botName: response.data.bot_name,
          botCountry: response.data.bot_country,
        });
        setStep("battle");
      } else {
        Alert(response.message || "Failed to start training battle");
      }
    } catch (error) {
      console.error("Failed to start training:", error);
      Alert("Failed to start training battle");
    } finally {
      setIsStarting(false);
    }
  }, [selectedPokemonIds, getAccessToken]);

  const handleRematch = useCallback(async () => {
    setBattleData(null);
    setStep("select-pokemon");
    // Immediately start another battle with the same Pokémon
    const accessToken = getAccessToken();
    if (!accessToken) {
      Alert("Authentication error. Please try again");
      return;
    }

    setIsStarting(true);
    try {
      const response = await startTrainingBattle(accessToken, {
        pokemon_ids: selectedPokemonIds,
        mode: "quick_training",
        format: config.format,
        cpu_type: config.cpuType,
      });

      if (response.ok && response.data) {
        setBattleData({
          battleLog: response.data.battle_log,
          botName: response.data.bot_name,
          botCountry: response.data.bot_country,
        });
        setStep("battle");
      } else {
        Alert(response.message || "Failed to start training battle");
      }
    } catch (error) {
      console.error("Failed to start training:", error);
      Alert("Failed to start training battle");
    } finally {
      setIsStarting(false);
    }
  }, [selectedPokemonIds, getAccessToken]);

  useEffect(() => {
    void getTeams();
  }, [getTeams]);

  // Custom setup bypasses team selection entirely
  if (config.cpuType === "custom") {
    return <DojoCustomSetupView />;
  }

  // Battle View
  if (step === "battle" && battleData) {
    return (
      <DojoBattleView
        battleLog={battleData.battleLog}
        botName={battleData.botName}
        botCountry={battleData.botCountry}
        onBack={handleBack}
        onRematch={handleRematch}
      />
    );
  }

  // Pokémon Selection View
  if (step === "select-pokemon" && selectedTeam) {
    const required = config.format === "1v1" ? 1 : 3;
    return (
      <div className="relative flex flex-col bg-pk-night-1/90 backdrop-blur-md pb-24 w-full h-full overflow-y-auto">
        {/* Header */}
        <div className="z-10 relative flex justify-between items-center mt-6 xs:mt-8">
          <div className="flex flex-col ml-4 p-2">
            <div className="relative">
              <h1 className="drop-shadow-lg font-black text-white text-3xl xs:text-4xl uppercase leading-none tracking-tight">
                PICK 3
              </h1>
              <div className="bg-pk-red-1 mt-1 w-12 xs:w-16 h-1 skew-x-[-20deg]" />
            </div>
            <p className="mt-2 text-pk-gray-4 text-sm">
              Select {required} Pokémon for training ({selectedPokemonIds.length}/{required})
            </p>
          </div>
          <button
            onClick={handleBack}
            className="mr-4 px-4 py-2 text-pk-gray-4 hover:text-white transition-colors"
          >
            ← Back
          </button>
        </div>

        {/* Pokémon Grid */}
        <div className="z-10 relative mx-auto px-4 pt-4 w-full max-w-md">
          <div className="gap-2 grid grid-cols-4">
            {selectedTeam.pokemons.map((pokemon) => {
              const isSelected = selectedPokemonIds.includes(pokemon.id);
              const selectionOrder = selectedPokemonIds.indexOf(pokemon.id) + 1;

              return (
                <button
                  key={pokemon.id}
                  onClick={() => handlePokemonToggle(pokemon.id)}
                  disabled={isStarting}
                  className={`
                    relative aspect-square rounded-lg p-1 transition-all duration-200
                    ${
                      isSelected
                        ? "bg-gradient-to-br from-pk-red-1 to-pk-red-2 scale-105 ring-2 ring-white"
                        : "bg-pk-night-2/80 hover:bg-pk-night-2"
                    }
                    ${selectedPokemonIds.length >= required && !isSelected ? "opacity-50" : ""}
                    disabled:opacity-50 disabled:cursor-not-allowed
                  `}
                >
                  <PokemonSprite
                    pokemon={pokemon}
                    className="w-full h-full object-contain"
                    style={{ scale: 1.5 }}
                    ignoreScale={true}
                  />
                  {isSelected && (
                    <div className="top-1 right-1 absolute flex justify-center items-center bg-white rounded-full w-5 h-5">
                      <span className="font-bold text-pk-night-1 text-xs">{selectionOrder}</span>
                    </div>
                  )}
                  <div className="right-0 bottom-0 left-0 absolute bg-black/60 px-1 py-0.5 rounded-b-lg">
                    <span className="block font-medium text-[10px] text-white truncate">
                      {pokemon.name || pokemon.base_pokemon_name}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Selected Preview */}
        {selectedPokemonIds.length > 0 && (
          <div className="z-10 relative mx-auto px-4 pt-6 w-full max-w-md">
            <div className="bg-pk-night-2/60 p-3 border border-white/10 rounded-lg">
              <h3 className="mb-2 text-pk-gray-4 text-xs uppercase tracking-wide">Your Team</h3>
              <div className="flex justify-center gap-4">
                {selectedPokemonIds.map((id, index) => {
                  const pokemon = selectedTeam.pokemons.find((p) => p.id === id);
                  if (!pokemon) return null;
                  return (
                    <div key={id} className="flex flex-col items-center">
                      <div className="bg-pk-night-1 p-1 rounded-lg w-16 h-16">
                        <PokemonSprite
                          pokemon={pokemon}
                          className="w-full h-full object-contain"
                          style={{ scale: 1.5 }}
                          ignoreScale={true}
                        />
                      </div>
                      <span className="mt-1 text-white text-xs">#{index + 1}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Start Button */}
        <div className="z-10 relative mx-auto px-4 pt-6 w-full max-w-md">
          <Button
            onClick={handleStartBattle}
            disabled={selectedPokemonIds.length !== required || isStarting}
            variant={selectedPokemonIds.length === required ? "red" : "night"}
            className="py-4 w-full"
          >
            <span className="font-bold text-lg">
              {isStarting
                ? "Starting..."
                : selectedPokemonIds.length === required
                  ? "START TRAINING!"
                  : `Select ${required - selectedPokemonIds.length} more`}
            </span>
          </Button>
        </div>
      </div>
    );
  }

  // Team Selection View (Default)
  return (
    <div className="relative flex flex-col bg-pk-night-1/90 backdrop-blur-md pb-24 w-full h-full overflow-y-auto">
      {/* Header */}
      <div className="z-10 relative flex justify-between items-center mt-6 xs:mt-8">
        <div className="flex flex-col ml-4 p-2">
          <div className="relative">
            <h1 className="drop-shadow-lg font-black text-white text-3xl xs:text-4xl uppercase leading-none tracking-tight">
              SELECT TEAM
            </h1>
            <div className="bg-pk-red-1 mt-1 w-12 xs:w-16 h-1 skew-x-[-20deg]" />
          </div>
          <p className="mt-2 text-pk-gray-4 text-sm">Choose your training squad</p>
        </div>
      </div>

      {/* Content */}
      <div className="z-10 relative flex flex-col gap-4 xs:gap-6 mx-auto px-4 pt-4 xs:pt-6 w-full max-w-md">
        {/* Teams container */}
        <div
          className="relative flex flex-col shadow-cartoon-sm border-4 border-gray-900 rounded-lg min-h-[200px] max-h-[60vh] overflow-hidden"
          style={{ backgroundColor: "#3b2c35" }}
        >
          <div className="z-10 absolute inset-0 bg-black/70 border-4 border-white/20 rounded-lg pointer-events-none" />
          <div
            className="z-0 absolute inset-0 rounded-lg pointer-events-none"
            style={{
              backgroundImage: "url(/assets/app-ui/glass-2.png)",
              backgroundSize: "cover",
              backgroundPosition: "left",
              backgroundRepeat: "repeat",
              mixBlendMode: "multiply",
              opacity: 0.35,
            }}
          />
          {!teams && (
            <div className="z-20 relative flex flex-col justify-center items-center py-12 animate-pulse">
              <span className="font-semibold text-white text-lg">Loading teams...</span>
              <p className="mt-2 text-pk-gray-4 text-xs">This should only take a moment</p>
            </div>
          )}
          {teams && teams.length > 0 && (
            <TeamList teams={teams} handleTeamClick={handleTeamSelect} />
          )}
          {teams && teams.length === 0 && (
            <div className="z-20 relative flex flex-col justify-center items-center py-12 text-center">
              <span className="font-semibold text-white text-lg">No teams yet</span>
              <p className="mt-2 max-w-sm text-pk-gray-4 text-sm">
                Create a team with at least 3 Pokémon to start training.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const TeamList = ({
  teams,
  handleTeamClick,
}: {
  teams: PokeTeam[];
  handleTeamClick: (team: PokeTeam) => void;
}) => {
  return (
    <div className="z-20 relative flex flex-col flex-1 gap-3 p-2 h-full min-h-0 overflow-x-hidden overflow-y-auto">
      {teams?.map((team) => {
        const isBlocked = team.pokemons.length < 3;
        return (
          <div
            key={team.id}
            className={`relative ${isBlocked ? "pointer-events-none" : ""}`}
          >
            {isBlocked && (
              <div className="z-30 absolute inset-0 flex justify-center items-center bg-black/60 backdrop-blur-[2px] rounded-lg">
                <span className="drop-shadow-md font-bold text-white text-sm uppercase tracking-wide">
                  Need 3+ Pokémon
                </span>
              </div>
            )}
            <div className={isBlocked ? "opacity-60" : ""}>
              <DojoTeamSelectCard team={team} {...(!isBlocked && { onClick: handleTeamClick })} />
            </div>
          </div>
        );
      })}
    </div>
  );
};
